import * as v from "valibot";

export const GenerateBytescaleJwtArgsSchema = v.object({
  jwtRs256Key: v.string(),
  accountId: v.string(),
  permissions: v.object({
    read: v.object({
      file: v.object({
        downloadFile: v.pipe(v.array(v.string()), v.minLength(1)),
        getFileDetails: v.boolean(),
      }),
      folder: v.object({
        getFolderDescription: v.boolean(),
        getFolderPublicPermissions: v.boolean(),
        getFolderStorageLayer: v.boolean(),
        listFolderChildren: v.boolean(),
      }),
    }),
    write: v.object({
      file: v.object({
        createFile: v.boolean(),
        deleteFile: v.boolean(),
        overwriteFile: v.boolean(),
      }),
      folder: v.object({
        createVirtualFolder: v.boolean(),
        deleteVirtualFolder: v.boolean(),
        setFolderDescription: v.boolean(),
        setFolderPublicPermissions: v.boolean(),
        setFolderStorageLayer: v.boolean(),
      }),
    }),
  }),
});

export type GenerateBytescaleJwtArgs = v.InferOutput<
  typeof GenerateBytescaleJwtArgsSchema
>;

export const UploadAuthenticationArgsSchema = v.object({
  publicKey: v.string(),
  uploadId: v.string(),
});

export type UploadAuthenticationArgs = v.InferOutput<
  typeof UploadAuthenticationArgsSchema
>;

export const ClientAuthenticationArgsSchema = v.object({
  accountId: v.string(),
  authUrl: v.string(),
  canUpload: v.boolean(),
});

export type ClientAuthenticationArgs = v.InferOutput<
  typeof ClientAuthenticationArgsSchema
>;

export const UploadFileArgsSchema = v.object({
  key: v.string(),
  file: v.instance(File),
  folderPath: v.string(),
  publicKey: v.string(),
  uploadId: v.string(),
  accountId: v.string(),
  canUpload: v.boolean(),
});

export type UploadFileArgs = v.InferOutput<typeof UploadFileArgsSchema>;

export const UploadFilesArgsSchema = v.object({
  key: v.string(),
  files: v.pipe(v.array(v.instance(File)), v.minLength(1)),
  folderPath: v.string(),
  publicKey: v.string(),
  parallelUploads: v.optional(v.number()),
  accountId: v.string(),
  canUpload: v.boolean(),
});

export type UploadFilesArgs = v.InferOutput<typeof UploadFilesArgsSchema>;

export const UploadFromUrlArgsSchema = v.object({
  key: v.string(),
  url: v.string(),
  folderPath: v.string(),
  publicKey: v.string(),
  uploadId: v.string(),
  accountId: v.string(),
  canUpload: v.boolean(),
});

export type UploadFromUrlArgs = v.InferOutput<typeof UploadFromUrlArgsSchema>;

export const UploadResponseSchema = v.object({
  accountId: v.string(),
  mime: v.string(),
  originalFileName: v.nullable(v.string()),
  tags: v.array(v.string()),
  filePath: v.string(),
  fileUrl: v.string(),
  lastModified: v.number(),
  size: v.number(),
});

export type UploadResponse = v.InferOutput<typeof UploadResponseSchema>;

export const UploadStatusSchema = v.variant("status", [
  v.object({
    progress: v.number(),
    rate: v.number(),
    status: v.literal("uploading"),
  }),
  v.object({ status: v.literal("error"), reason: v.string() }),
  v.object({
    status: v.literal("completed"),
    bytescaleFileName: v.string(),
    response: UploadResponseSchema,
  }),
  v.object({ status: v.picklist(["started", "initialised"]) }),
]);

export type UploadStatus = v.InferOutput<typeof UploadStatusSchema>;

export const UploadSchema = v.intersect([
  v.object({
    key: v.string(),
    file: v.optional(v.instance(File)),
    uploadId: v.string(),
  }),
  UploadStatusSchema,
]);

export type Upload = v.InferOutput<typeof UploadSchema>;
